import {
    Injectable, inject
} from "@angular/core";
import {
    FileRequestSubject,
    FileResponse,
    ProductUtil,
    RequestUtil
} from "@shopliftr/common-js/shared";
import {
    ProductImageUploadOptions, ProductImageUploadType
} from "@shopliftr/common-js/admin";
import {
    Language, MimeType
} from "@shopliftr/common-js/data";
import {
    Observable, throwError
} from "rxjs";

import { IInternalApiFile } from "../../../../interfaces/internal-api-file";
import { InternalApiFileRequestService } from "../../request/internal-api/internal-api-file-request.service";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";

/**
 * API File Service for ProductImage file operations.
 *
 * Handles image uploads for both standard products and batch products via a
 * single `upload$` method, distinguished by the `isBatchProduct` flag, and
 * delegates HTTP communication to InternalApiFileRequestService.
 *
 * @pattern API File Service / ProductImage File Management
 * @providedIn root
 * @implements {IInternalApiFile}
 */
@Injectable({ providedIn: "root" })
export class ProductImageApiFileService implements IInternalApiFile {

    readonly fileRequestSubject = FileRequestSubject.ProductImage;

    private readonly _internalApiFileRequestService = inject(InternalApiFileRequestService);

    /**
     * Uploads an image for a standard product or a batch product.
     *
     * @param id - Product UPC code, or batch product record identifier when `isBatchProduct` is true
     * @param imageFile - File blob to upload
     * @param language - Optional language for localized images
     * @param isBatchProduct - When true, uploads against the batch product image type; defaults to false (standard product)
     * @returns Observable that emits the file upload response
     */
    upload$(id: string, imageFile: Blob, language?: Language, isBatchProduct = false): Observable<FileResponse> {

        if (isBatchProduct) {
            if (!id) {
                return throwError(() => new InvalidParametersException("No UPC was provided; image cannot be uploaded"));
            }
        }
        else if (!id || !imageFile) {
            return throwError(() => new InvalidParametersException("Upc and imageFile required"));
        }

        const options = new ProductImageUploadOptions().setDefaults();
        options.id = id;
        options.type = isBatchProduct ? ProductImageUploadType.BatchProduct : ProductImageUploadType.Product;
        if (language) {
            options.language = language;
        }
        const filePath = ProductUtil.getImageFilePath(id, imageFile.type as MimeType, language);
        const request = RequestUtil.createFileUploadRequest(
            FileRequestSubject.ProductImage, filePath, imageFile.type as MimeType, options
        );
        return this._internalApiFileRequestService.uploadFile$(request, imageFile);
    }
}
